/* eslint-disable import/no-mutable-exports */
import store from '@carvana/purchase-store';
import Cookies from 'js-cookie';
import {
  DashboardModalActions,
  routeTo,
  GetAssignedBucket
} from '@carvana/purchase-helpers';
import { configureExperimentsFullstory } from '@carvana/purchase-utils';
import * as Services from '../services';
import { FetchCustomerRequests } from '../components/customer-request/ducks/actions';
import {
  FetchInsuranceReferral,
  FetchInsuranceConsent,
  FetchInsuranceProfile
} from '../components/insurance-landing/ducks/actions';
import { FetchOrderOptions } from '../components/order-placed/components/contracts/ducks/actions';
import {
  FetchVerifications,
  FetchPaymentCollection
} from '../components/order-placed/components/funds-verification/ducks/actions';
import { FetchOPDNotifications } from '../components/order-placed/components/notifications/ducks/action';
import { GetNotarizeStatus } from '../components/order-placed/components/notarize/ducks/actions';
import CongratulationsModal from '../components/order-placed/components/modals/components/congratulations-modal';
import { Content } from '../components/order-placed/components/modals/components/congratulations-modal/congratulations-modal.styles';

import * as types from './action-types';
import {
  determineInsuranceQuoteRedirect,
  shouldRedirectToInsuranceQuote
} from '../helpers/mobile-logic';

export const OpenCongratsModal = orderId => {
  const orderPlacedCongratsModalCookie = Cookies.get(
    'orderPlacedCongratsModal'
  );
  const sameOrder = orderPlacedCongratsModalCookie === orderId;

  if (!orderPlacedCongratsModalCookie || !sameOrder) {
    Cookies.set('orderPlacedCongratsModal', orderId);
    DashboardModalActions.OpenModal({
      modalContentStyles: Content,
      headerText: 'SUCCESS!',
      ModalChildren: CongratulationsModal
    });
  }
};

const trackUser = experiments => (dispatch, getState) => {
  const {
    vehicle: { stockNumber } = {},
    core: { userId, email, name } = {},
    dashboard: { status: { purchaseId } = {} } = {}
  } = getState();

  const fullstoryExperiments = configureExperimentsFullstory(experiments);

  return dispatch({
    type: '@@analytics/ANALYTICS_TRACK_USER',
    payload: Promise.resolve(),
    meta: {
      analytics: {
        type: 'trackUser',
        name: 'Get Dashboard Route Next Step',
        fullstory: {
          userId,
          email,
          name,
          stockNumber,
          purchaseId,
          experiments: fullstoryExperiments
        },
        clients: ['fullstory']
      }
    }
  });
};

export const GetUsersExperiments = () => {
  const { userId } = store.getState().core;
  store
    .dispatch({
      type: types.GET_USER_EXPERIMENTS,
      async payload() {
        return Services.getUsersExperiments(userId);
      }
    })
    .then(({ value }) => {
      return store.dispatch(trackUser(value?.content));
    });
};

export const getAssignedBucket = experimentName => {
  const { userId } = store.getState().core;

  return GetAssignedBucket({
    environment: process.env.NODE_ENV,
    experimentName,
    identifier: userId
  })
    .then(({ assignedBucket }) => assignedBucket)
    .catch(() => 'control');
};

const determineNRSF = async method => {
  let isNRSF = false;
  if (method === 'ThirdParty') {
    isNRSF = getAssignedBucket('pp-nrsf-finance-saf').then(
      res => res === 'nrsf-finance-saf'
    );
  } else {
    isNRSF = getAssignedBucket(
      'finance-nonrefundable-shipping-fees-dec-2022'
    ).then(res => res === 'nonrefundable-shipping-fees');
  }
  return isNRSF;
};

export const DetermineNRSF = () => dispatch => {
  const { dashboard } = store.getState() || {};
  const method = dashboard?.orderSummary?.paymentMethod?.method;
  dispatch({
    type: types.DETERMINE_NRSF,
    async payload() {
      return determineNRSF(method);
    }
  });
};

const determineContractToCR = async () => {
  let isContractToCR = getAssignedBucket(
    'pp-contracts-to-customerrequests'
  ).then(res => res === 'contracts2cr');
  return isContractToCR;
};

export const DetermineContractToCR = () => dispatch => {
  dispatch({
    type: types.DETERMINE_CONTRACT_TO_CR,
    async payload() {
      return determineContractToCR();
    }
  });
};

const determineContractToCRPhase2 = async () => {
  let isContractToCR = getAssignedBucket(
    'pp-contracts-to-customerrequests'
  ).then(res => res === 'contracts2cr-phase2');
  return isContractToCR;
};

export const DetermineContractToCRPhase2 = () => dispatch => {
  dispatch({
    type: types.DETERMINE_CONTRACT_TO_CR_PHASE_2,
    async payload() {
      return determineContractToCRPhase2();
    }
  });
};

export const FetchOrderSummary = () =>
  store.dispatch({
    type: types.ORDER_SUMMARY_FETCH,
    async payload() {
      return Services.fetchOrderSummary();
    }
  });

export const GetPaymentSummary = () => dispatch =>
  dispatch({
    type: types.GET_PAYMENT_SUMMARY,
    async payload() {
      return Services.getPaymentSummary();
    }
  });

export const ToggleOrderSummary = () => {
  const currentlyShowingOrderSummary =
    store.getState()?.orderPlaced?.root?.showOrderSummary ?? false;

  const hasLoadedPaymentSummary =
    store.getState()?.orderPlaced?.root?.paymentSummary?.hasLoaded ?? false;

  return store.dispatch({
    type: types.TOGGLE_ORDER_SUMMARY,
    payload() {
      if (!currentlyShowingOrderSummary && !hasLoadedPaymentSummary)
        store.dispatch(GetPaymentSummary());
    }
  });
};

export const GetVehicleSoldOnLotById = () => {
  const { stockNumber } = store?.getState()?.vehicle || {};
  return store
    .dispatch({
      type: types.GET_VEHICLE_SOLD_ON_LOT,
      async payload() {
        return Services.getVehicleSoldOnLotById(stockNumber);
      }
    })
    .then(res => {
      if (res.value) store.dispatch(routeTo('/vehicle/sold-on-lot', 'replace'));
    })
    .catch(() => {});
};

export const LoadOrderPlaced = async () => {
  return Promise.allSettled([
    FetchInsuranceReferral().then(({ value }) =>
      determineInsuranceQuoteRedirect(value)
    ),
    store.dispatch(FetchInsuranceConsent()),
    store.dispatch(FetchInsuranceProfile()),
    FetchOrderOptions(),
    FetchVerifications(),
    store.dispatch(FetchOPDNotifications()),
    store.dispatch(FetchCustomerRequests()),
    FetchPaymentCollection(),
    GetNotarizeStatus(),
    GetVehicleSoldOnLotById(),
    store.dispatch(DetermineNRSF()),
    store.dispatch(DetermineContractToCR()),
    store.dispatch(DetermineContractToCRPhase2()),
    store.dispatch(GetPaymentSummary())
  ]).finally(() => {
    if (shouldRedirectToInsuranceQuote()) {
      store.dispatch(routeTo('/order-placed/customer-request/insurance-quote'));
    }

    store.dispatch({ type: types.ORDER_PLACED_LOADED });
  });
};

export const SetSelectedCustomerRequest = payload => dispatch =>
  dispatch({
    type: types.SELECTED_CUSTOMER_REQUEST,
    payload
  });

export const SetToastMessage = payload => dispatch =>
  dispatch({
    type: types.SET_ORDER_PLACED_DASHBOARD_TOAST,
    payload
  });

export const ResetToastMessage = () => dispatch =>
  dispatch({
    type: types.RESET_ORDER_PLACED_TOAST
  });
